import React from 'react';
import {
  Avatar,
  Box,
  Chip,
  Icons,
  Typography,
  useTheme,
} from '@ublend-npm/aulaui-core';
import VisibilityOffOutlinedIcon from '@mui/icons-material/VisibilityOffOutlined';
import { STUDENT_ROLE } from '@core/constants/space';
import { HomeViewRole } from '@app/constants/home';
import { HomeUpcomingAssignment } from '@core/assignments/api/homeUpcomingAssignments';

const STRINGS = {
  dueDate: 'Due Date',
  noDueDate: 'No due date',
  submitted: 'Submitted',
};

export const formatAssignmentDueDate = (dueDate: string | null) => {
  if (!dueDate) {
    return STRINGS.noDueDate;
  }

  const date = new Date(dueDate);

  if (Number.isNaN(date.getTime())) {
    return STRINGS.noDueDate;
  }

  return date.toLocaleDateString('en-GB');
};

type UpcomingAssignmentRowProps = {
  assignment: HomeUpcomingAssignment;
  viewRole: HomeViewRole;
  variant?: 'widget' | 'modal';
};

const UpcomingAssignmentRow = ({
  assignment,
  viewRole,
  variant = 'modal',
}: UpcomingAssignmentRowProps) => {
  const { palette } = useTheme();
  const isWidget = variant === 'widget';
  const formattedDueDate = formatAssignmentDueDate(assignment.dueDate);

  const statusIndicator =
    assignment.isHidden && viewRole !== STUDENT_ROLE ? (
      <VisibilityOffOutlinedIcon
        sx={{ fontSize: 20, color: palette.text.disabled }}
        aria-label="Hidden assignment"
      />
    ) : (
      <Box
        role="status"
        aria-label={assignment.status}
        sx={{
          width: 10,
          height: 10,
          borderRadius: '50%',
          bgcolor:
            assignment.status === 'DueSoon'
              ? palette.warning.main
              : palette.info.main,
          flexShrink: 0,
        }}
      />
    );

  if (isWidget) {
    return (
      <Box
        p={2}
        borderRadius={2}
        sx={{ backgroundColor: palette.background.default }}
      >
        <Box
          display="flex"
          alignItems="flex-start"
          justifyContent="space-between"
          gap={1}
        >
          <Typography variant="subtitle2" fontWeight="medium">
            {assignment.title}
          </Typography>
          <Box display="flex" alignItems="center" gap={0.75}>
            {viewRole === STUDENT_ROLE && assignment.isSubmitted && (
              <Chip
                size="small"
                label={STRINGS.submitted}
                role="status"
                aria-label={STRINGS.submitted}
                sx={{ flexShrink: 0 }}
              />
            )}
            {statusIndicator}
          </Box>
        </Box>
        <Typography variant="body2" sx={{ opacity: 0.8 }}>
          {assignment.courseName} • {assignment.status} • {formattedDueDate}
        </Typography>
      </Box>
    );
  }

  return (
    <Box
      display="flex"
      alignItems="center"
      gap={2}
      py={2}
      sx={{
        borderBottom: `1px solid ${palette.divider}`,
        '&:last-child': {
          borderBottom: 'none',
        },
      }}
    >
      <Avatar
        sx={{
          width: 40,
          height: 40,
          bgcolor: palette.action.hover,
          color: palette.text.secondary,
        }}
      >
        <Icons.AssignmentOutlined fontSize="small" />
      </Avatar>

      <Box flex={1} minWidth={0}>
        <Box
          display="flex"
          alignItems="flex-start"
          justifyContent="space-between"
          gap={1}
        >
          <Typography variant="body1" fontWeight={600} color="text.primary">
            {assignment.title}
          </Typography>
          <Box display="flex" alignItems="center" gap={0.75}>
            {viewRole === STUDENT_ROLE && assignment.isSubmitted && (
              <Chip
                size="small"
                label={STRINGS.submitted}
                role="status"
                aria-label={STRINGS.submitted}
                sx={{ flexShrink: 0 }}
              />
            )}
            {statusIndicator}
          </Box>
        </Box>
        <Typography variant="body2" color="text.secondary" mt={0.5}>
          {STRINGS.dueDate}: {formattedDueDate}
        </Typography>
        <Typography variant="body2" color="text.secondary">
          {assignment.courseName}
        </Typography>
      </Box>
    </Box>
  );
};

export default UpcomingAssignmentRow;
